// Pure reconciliation logic for cross-device wardrobe sync — no Supabase / no
// side effects, so it's unit-testable in isolation. wardrobe-sync.ts wraps this
// with the actual network I/O.
//
// Rules (last-write-wins by `updated_at`, but loss-averse):
//   - owned   : UNION(local, remote)  — never lose an outfit you bought
//   - ledger  : UNION(local, remote)  — more awarded keys = stricter anti-farm
//   - lifetime: MAX(local, remote)    — lifetime Bones is monotonic
//   - balance : the side updated more recently wins
//   - equipped: the side updated more recently wins

import { EMPTY_EQUIPPED, type Equipped } from "./wardrobe-store"

export interface WardrobeState {
  owned: string[]
  equipped: Equipped
  balance: number
  lifetime: number
  ledger: string[]
}

export interface RemoteRow {
  owned: string[] | null
  equipped: unknown
  bones_balance: number | null
  bones_lifetime: number | null
  bones_ledger: unknown
  updated_at: string
}

export function asStrArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []
}

export function normEquipped(v: unknown): Equipped {
  const o = v && typeof v === "object" ? (v as Record<string, unknown>) : {}
  const pick = (k: keyof Equipped) => (typeof o[k] === "string" ? (o[k] as string) : null)
  return { look: pick("look"), body: pick("body"), hat: pick("hat"), neck: pick("neck"), accessory: pick("accessory"), effect: pick("effect") }
}

/** Pure reconciliation. `localSyncedAt` is the remote updated_at we last saw on
 * this device (null = never synced here, so remote is treated as newer). */
export function mergeWardrobe(
  local: WardrobeState,
  remote: RemoteRow,
  localSyncedAt: string | null,
): { merged: WardrobeState; changedLocal: boolean } {
  const remoteNewer = !localSyncedAt || remote.updated_at > localSyncedAt

  const owned = Array.from(new Set([...(local.owned || []), ...asStrArr(remote.owned)]))
  const ledger = Array.from(new Set([...(local.ledger || []), ...asStrArr(remote.bones_ledger)]))
  const lifetime = Math.max(Number(local.lifetime) || 0, Number(remote.bones_lifetime) || 0)

  const balance = remoteNewer ? Number(remote.bones_balance) || 0 : Number(local.balance) || 0
  const equipped = remoteNewer ? { ...EMPTY_EQUIPPED, ...normEquipped(remote.equipped) } : local.equipped

  const merged: WardrobeState = { owned, equipped, balance, lifetime, ledger }

  const equippedChanged = JSON.stringify(equipped) !== JSON.stringify(local.equipped)
  const changedLocal =
    owned.length !== (local.owned || []).length ||
    ledger.length !== (local.ledger || []).length ||
    lifetime !== (Number(local.lifetime) || 0) ||
    balance !== (Number(local.balance) || 0) ||
    equippedChanged

  return { merged, changedLocal }
}
